import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { event } from '@uhg-abyss/web/tools/event';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const JobDescription = styled('p', {
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '20px',
  paddingLeft: '36px',
  lineHeight: 1.25,
  marginTop: '8px',
  fontFamily: '$primary',
});

const TextAreaLabel = styled('div', {
  justifyContent: 'space-between',
});

const MandatorySpan = styled('span', {
  fontSize: '16px',
  color: '$redDvz1',
});

export const JobView = ({
   id,
  name,
  desc,
  isNpiRequired,
  isAPIMarketPlaceUser,
}) => {

  const form = useForm();


  const lagoonContext = React.useContext(LagoonContext);

  const featureToggles = lagoonContext.getTable('feature-toggles');


  const handleRCMChange = (value) => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      event('RCM_SELECTION', {
        interactionName:
          'Working on behalf of a Revenue Cycle Management (RCM) or other billing company',
        interactionValue: value === 'Y' ? 'Yes' : 'No',
        interactionContext: 'Job Functions',
      });
    }

    if (value === 'N') {
      form.setValue(`SURegistration.job_company_txt_${id}`, '');
    }
  };


  const labelText = () => {
    return (
      <TextAreaLabel>
        National Provider Identification #{' '}
        <MandatorySpan>*</MandatorySpan>
      </TextAreaLabel>
    );
  };

  const adminSelected = form.getValues('SURegistration.jobFunction') === 'Administrative Staff';
  const jobFunction = form.getValues('SURegistration.jobFunction');
  const rcmRadioValue = form.getValues(`SURegistration.job_radio_${id}`);

  return (
    <React.Fragment>
      <RadioGroup.Radio id={`job_radio_${id}`} label={name} subtext={desc} value={name} />
        <JobDescription>{desc}</JobDescription>
        {adminSelected && name === 'Administrative Staff' && !isAPIMarketPlaceUser && (
          <div
            style={{
              marginLeft: '36px',
            }}
          >
            <RadioGroup
              id={`job_radio_${id}`}
              label="Are you working on behalf of a Revenue Cycle Management (RCM) or other billing company?"
              model={`SURegistration.job_radio_${id}`}
              onChange={(e) => {
                handleRCMChange(e.target.value);
                form.trigger(`SURegistration.job_radio_${id}`);
                form.trigger(`SURegistration.job_company_txt_${id}`);
              }}
              display="row"
              validators={{ required: 'Please select Yes or No' }}
            >
              <RadioGroup.Radio label="Yes" value="Y" />
              <RadioGroup.Radio label="No" value="N" />
            </RadioGroup>
            <TextInput
              id="companyname-input"
              label="Company Name"
              model={`SURegistration.job_company_txt_${id}`}
              aria-label="type here to enter company name"
              validators={{
                validate: val => {
                  const radio = form.getValues(`SURegistration.job_radio_${id}`)
                  if (jobFunction === name && radio === 'Y' && !val) {
                    return 'Please enter Company Name'
                  }
                }
              }}
              onChange={() => {return form.trigger(`SURegistration.job_company_txt_${id}`)}}
              maxLength={50}
              isDisabled={rcmRadioValue !== 'Y'}
              css={{
                'abyss-text-input-root': { width: '350px' },
              }}
              isClearable
              helper={
                <Tooltip content="Identifying RCM/billing company associates will better allow us to tailor your UHC digital experience.">
                  <Button
                    css={{
                      'abyss-button-root': { height: '14px', width: '14px' },
                    }}
                    aria-label="Tooltip information"
                    rounded
                  >
                    <IconSymbol icon="question_mark" color="$white" size={12} />
                  </Button>
                </Tooltip>
              }
            />
          </div>
        )}
        {isNpiRequired ? (
          <div
            style={{
              marginLeft: '36px',
              width: '235px',
              marginBottom: '30px',
              marginTop: '-10px',
            }}
          >
            <TextInput
              id={`job_ctrl_npi_text_${id}`}
              model={`SURegistration.job_npi_txt_${id}`}
              label={labelText()}
              aria-label=""
              maxLength={10}
              css={{
                'abyss-text-input-label': {
                  fontSize: '14px',
                },
              }}
              formatter={(value) => {
                return value.replace(/[^0-9.]/g, '');
              }}
              onChange={() => {return form.trigger(`SURegistration.job_npi_txt_${id}`)}}
              validators={{
                validate: (value) => {
                  if (jobFunction === name && !value) {
                    return 'Invalid NPI value';
                  }
                  if (value && value.length > 0 && value.length < 10) {
                    return 'Invalid NPI value';
                  }
                }
              }}
              isDisabled={jobFunction !== name}
              isClearable
            />
          </div>
        ) : null}
    </React.Fragment>
  )
}

JobView.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  isNpiRequired: PropTypes.bool.isRequired,
  isAPIMarketPlaceUser: PropTypes.bool,
};

JobView.defaultProps = {
  isAPIMarketPlaceUser: false,
};